"use client"

import { Radio, RadioGroup, Stack } from "@chakra-ui/react"
import axios from "axios"
import { useEffect, useRef, useState } from "react"
import DatePicker from "react-datepicker"
import { NotificationManager } from "react-notifications"
import Select from "react-select"
import gif from "../../assets/gif/inbox.gif"
import useAuth from "../../hooks/useAuth"
import { BASE_URL } from "../../utils/Element"
import Modal from "../Modal/Modal"

const initialData = {
  bds: null,
  broker: null,
  created_at: "",
  lead_source: null,
  lead_type: "",
  contact_id: "",
  link: "",
  lead_status: "2",
  portfolio: ""
}

const MarkAsLeadConfirmationModal = ({ showModal, from, onClose, handleAction, isLead, contactDetails }) => {
  const [value, setValue] = useState()
  const [leadSourceOptions, setLeadSourceOptions] = useState([])
  const [config] = useAuth()
  const [loading, setLoading] = useState(false)
  const [bdsOptions, setBdsOptions] = useState([])
  const [linkOptions, setLinkOptions] = useState([])
  const [brokerOptions, setBrokerOptions] = useState([])
  const [formData, setFormData] = useState(initialData)
  const [errors, setErrors] = useState({})
  const selectRef = useRef(null)

  const statusOptions = [
    { value: 2, label: "Cold" },
    { value: 3, label: "Warm" },
    { value: 1, label: "Hot" },
  ]


  const validateForm = () => {
    const newErrors = {}

    if (!formData.broker) {
      newErrors.broker = "Broker is required"
    }

    if (!formData.lead_source) {
      newErrors.lead_source = "Lead source is required"
    }

    if (!formData.lead_status) {
      newErrors.lead_status = "Lead status is required"
    }

    if (!formData.lead_type) {
      newErrors.lead_type = "Lead type is required"
    }

    if (formData.lead_type && !formData.link) {
      newErrors.link = "Property/Acquisition Criteria is required"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleConfirm = () => {
    if (!validateForm()) {
      return
    }

    const payload = {
      ...formData,
      lead_type: formData.lead_type === "acquisition" ? 1 : 0, // Convert string to number
      type: from === "lead" ? 0 : 1,
    }

    console.log("Payload sent to API:", payload)
    handleAction(payload)
  }

  const handleChange = (value, name) => {
    setErrors((prev) => ({ ...prev, [name]: "" }))
    setFormData((prev) => ({ ...prev, [name]: value || null }))
  }

  const fetchLeadSources = () => {
    setLoading(true)
    axios
      .get(`${BASE_URL}/get-datalisting?list=lead_sources`, config)
      .then((res) => {
        const data = res?.data?.data
        const options = data?.lead_sources?.map((el) => ({ value: el?.id, label: el.name }))
        setLeadSourceOptions(options)
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message)
        }
      })
      .finally(() => setLoading(false))
  }

  const fetchUsers = () => {
    setLoading(true)
    axios
      .get(`${BASE_URL}/get-datalisting?list=users`, config)
      .then((res) => {
        const data = res?.data?.data
        const options = data?.map((el) => ({
          value: el?.id,
          label: `${el?.first_name} ${el?.last_name}`,
          role: el?.role_id,
        }))
        setBdsOptions(options)
        setBrokerOptions(options?.filter((el) => el.role === 3))
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message)
        }
      })
      .finally(() => setLoading(false))
  }

  const fetchLinks = () => {
    setLoading(true)
    axios
      .get(`${BASE_URL}/get-ContactData/${contactDetails?.id}`, config)
       .then((res) => {
        const data = res?.data?.data;
        const properties = data?.properties;
        const acquisitions = data?.acquisitions;
        const portfolios = data?.portfolios;
        const propertyOptions = properties?.map((el) => ({ value: el?.id, label: el?.property_name, desc: `${el.street_address}, ${el.city}, ${el.state}` }));
        const acquisitionOptions = acquisitions?.map((el) => ({ value: el?.id, label: el?.property_type?.[0]?.type, minPrice: el?.min_price, maxPrice: el?.max_price }));
        const portfolioOptions = portfolios?.map((el) => ({ value: el?.id, label: el?.name, desc: `` }));

        if (formData.lead_type === "acquisition" && formData.portfolio !== 1) {
          setLinkOptions(acquisitionOptions);
        }else if (formData.lead_type === "disposition" && formData.portfolio !== 1) {
          setLinkOptions(propertyOptions);
        } else{
          setLinkOptions(portfolioOptions);
        }
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message)
        }
      })
      .finally(() => setLoading(false))
  }

  const formatOptionLabel = ({ value, label, minPrice, maxPrice, desc }) => (
    <div>
      <div>{label}</div>
      {desc && <p className="dark-M">{desc}</p>}
      {(minPrice || maxPrice) && (
        <div className="flex gap-2 dark-M">
          {minPrice && <div>Min Price-{minPrice}</div>}/{maxPrice && <div>Max Price-{maxPrice}</div>}
        </div>
      )}
    </div>
  )

  const handleLeadTypeChange = (value) => {
    console.log("Selected lead type:", value)
    setErrors((prev) => ({ ...prev, lead_type: "", link: "" }))
    setFormData({
      ...formData,
      lead_type: value,
      link: "",
    })

    console.log("Updated lead_type in formData:", value)
  }

  useEffect(() => {
    if (showModal) {
      if (formData?.contact !== "") {
        fetchLinks()
      }
    }
  }, [showModal, formData?.contact, formData?.lead_type ,formData?.portfolio])

  const handlePortfolioChange = (value) => {
    const portfolioStatus = formData.portfolio ? 0 : 1;
    setFormData({ ...formData, portfolio: portfolioStatus, link: "" });
  };

  useEffect(() => {
    if (showModal) {
      fetchUsers()
      fetchLeadSources()
      setFormData(initialData)
      setErrors({})
    }
  }, [showModal])

  return (
    <Modal show={showModal} onClose={onClose}>
      <form className="py-3">
        <div className="flex justify-center mt-[-20px]">
          <img src={gif || "/placeholder.svg"} alt="" height={120} width={120} />
        </div>

        <div className="flex-1 mt-4 text-center w-30">
          <p className="head-1 dark-H font-bold">{isLead ? "Mark as" : "Unmark"} lead?</p>
          <p className="py-1 body-N dark-M mb-3">
            Are you sure to {isLead ? "mark as" : "unmark"} lead in this contact? This action can be reversed anytime.
          </p>
        </div>

        <div className="mt-3">
          <div className="flex mt-4 gap-4">
            <div className="w-[48%] mt-4">
              <label className="dark-H head-4 mb-2">BDS</label>
              <Select
                ref={selectRef}
                className="body-N"
                options={[{ label: "None", value: null }, ...bdsOptions]}
                placeholder="Select"
                value={bdsOptions.find((option) => option?.value === formData.bds) || null}
                onChange={(option) => handleChange(option?.value || null, "bds")}
              />
              {errors?.bds && <p className="body-S red-D">{errors?.bds}</p>}
            </div>

            <div className="w-[48%] mt-4">
              <label className="dark-H head-4 mb-2">
                Broker
              </label>
              <Select
                ref={selectRef}
                className="body-N"
                options={[{ label: "None", value: null }, ...brokerOptions]}
                placeholder="Select"
                value={brokerOptions.find((option) => option?.value === formData.broker) || null}
                onChange={(option) => handleChange(option?.value || null, "broker")}
              />
              {/* Fixed: Use consistent error field name */}
              {errors?.broker && <p className="body-S red-D">{errors?.broker}</p>}
            </div>
          </div>

          <div className="flex mt-4 gap-4">
            <div className="w-[48%] mt-3">
              <label className="dark-H head-4 mb-2">Lead Date</label>
              <DatePicker
                className="body-N"
                dateFormat="MM/dd/yyyy"
                placeholderText="MM/DD/YYYY"
                selected={formData.created_at ? new Date(formData.created_at) : null}
                onChange={(value) => handleChange(value, "created_at")}
                maxDate={new Date()}
              />
              {errors?.created_at && <p className="body-S red-D">{errors?.created_at}</p>}
            </div>

            <div className="w-[48%] mt-4">
              <label className="dark-H head-4 mb-2">
                Lead Source<span className="red-D">*</span>
              </label>
              <Select
                className="body-N"
                options={[{ label: "None", value: null }, ...leadSourceOptions]}
                placeholder="Select"
                value={leadSourceOptions?.find((option) => option.value === formData.lead_source) || null}
                onChange={(option) => handleChange(option?.value || null, "lead_source")}
              />
              {/* Fixed: Use consistent error field name */}
              {errors?.lead_source && <span className="body-S red-D">{errors?.lead_source}</span>}
            </div>
          </div>

          
            {/* <label className="dark-H head-4 mb-2 required:*:">
              Lead Status<span className="red-D">*</span>
            </label> */}
            {/* <div className="mt-1.5">
              <Select
                options={statusOptions}
                value={statusOptions.find((option) => option.value === formData.lead_status) || null}
                onChange={(option) => handleChange(option?.value || null, "lead_status")}
                placeholder="Select Status"
                className="body-N"
              />
            </div> */}
            {/* Fixed: Use correct error field name */}
            {/* {errors?.lead_status && <span className="body-S red-D">{errors?.lead_status}</span>} */}
          

          <div className="flex gap-3 mt-4">
            <label className="dark-H head-4 mb-2">
              Lead Type<span className="red-D">*</span>
            </label>
            <RadioGroup onChange={handleLeadTypeChange} value={formData?.lead_type}>
              <Stack direction="row" gap={5}>
                <Radio value="disposition">Disposition</Radio>
                <Radio value="acquisition">Acquisition</Radio>
              </Stack>
            </RadioGroup>
          </div>
          {/* Fixed: Add error display for lead_type */}
          {errors?.lead_type && <span className="body-S red-D">{errors?.lead_type}</span>}

          {formData?.lead_type !== "" && (
            <>

              <div className="mt-6">
                <label className="container" style={{ marginBottom: "0px" }}>
                  <p style={{ paddingLeft: "30px" }} className="dark-H head-4">Portfolio</p>
                  <input
                    type="checkbox"
                    checked={formData?.portfolio}
                    className="body-N"
                    onChange={(value) => {
                      handlePortfolioChange(value);
                    }}
                  />
                  <span className="checkmark"></span>
                </label>
              </div>

              <div className="mt-6">
                <label className="dark-H head-4 mb-2 required:*:">
                  Property /Acquisition Criteria<span className="red-D">*</span>
                </label>
                <div className="mt-1.5">
                  <Select
                    className="body-N"
                    options={linkOptions}
                    formatOptionLabel={formatOptionLabel}
                    placeholder={"Select"}
                    value={linkOptions.find((option) => option.value === formData.link)}
                    onChange={(option) => {
                      handleChange(option?.value, "link")
                    }}
                  />
                </div>
                {/* Fixed: Use correct error field name */}
                {errors?.link && <span className="body-S red-D">{errors?.link}</span>}
              </div>

            </>
          )}
        </div>

        <div className="mt-6 flex justify-center">
          <button type="button" onClick={handleConfirm} className="save-button light-L head-5 green-bg-H">
            Confirm
          </button>
          <button type="button" onClick={onClose} className="green-H body-N ml-5">
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  )
}

export default MarkAsLeadConfirmationModal
